import React, { useRef, useState } from 'react';
import {
  Download,
  Loader2,
  MoreVertical,
  RefreshCw,
  Server,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useViewModelReady } from '../../../../hooks/useViewModel';
import { PortalMenu } from '../../../../components/PortalMenu';
import type {
  AdminHarborRunnerToken,
  HarborRunnerTokenStatus,
} from '../../../../services/harborRunnerTokens';
import type {
  HarborArtifactDownload,
  HarborJob,
  HarborJobArtifact,
  HarborJobArtifactId,
} from '../../../../services/harborRuns';
import {
  HARBOR_ADMIN_PAGE_SIZE,
  useHarborRunnersToolViewModel,
} from './viewModel';

const STATUS_LABELS: Record<HarborRunnerTokenStatus, string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
};

type HarborRunnersTab = 'runners' | 'jobs';

export function HarborRunnersTool({ mobileNavTrigger }: { mobileNavTrigger?: React.ReactNode }) {
  const vm = useHarborRunnersToolViewModel();
  useViewModelReady(vm.initialize);
  const [activeTab, setActiveTab] = useState<HarborRunnersTab>('runners');

  return (
    <section className="min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Server className="h-5 w-5 text-primary" aria-hidden="true" />
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Harbor Runners</h1>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Issued Harbor runner credentials and their current status.
          </p>
        </div>
        {mobileNavTrigger}
      </div>

      {vm.busy ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <nav className="mt-6 flex gap-4 border-b border-gray-200" aria-label="Harbor views">
            <HarborTab
              active={activeTab === 'runners'}
              count={`${vm.runners.length}${vm.runnersHasMore ? '+' : ''}`}
              onClick={() => setActiveTab('runners')}
            >
              Runners
            </HarborTab>
            <HarborTab
              active={activeTab === 'jobs'}
              count={`${vm.jobs.length}${vm.jobsHasMore ? '+' : ''}`}
              onClick={() => setActiveTab('jobs')}
            >
              Harbor jobs
            </HarborTab>
          </nav>

          <div className="mt-6 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              {activeTab === 'runners'
                ? pageSummary(vm.runnerPage, vm.runners.length, 'runner')
                : pageSummary(vm.jobsPage, vm.jobs.length, 'job')}
            </p>
            <button
              type="button"
              onClick={() => void vm.refresh()}
              disabled={vm.loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${vm.loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {vm.error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {vm.error}
            </div>
          )}

          {activeTab === 'runners' ? (
            <>
              <HarborRunnerTable runners={vm.runners} />
              <ListPagination
                page={vm.runnerPage}
                itemCount={vm.runners.length}
                hasMore={vm.runnersHasMore}
                loading={vm.loading}
                onPrevious={vm.previousRunnerPage}
                onNext={vm.nextRunnerPage}
              />
            </>
          ) : (
            <>
              <HarborJobsTable
                jobs={vm.jobs}
                jobActionId={vm.jobActionId}
                onCancel={vm.cancelJob}
                onDelete={vm.deleteJob}
                onDownload={async (jobId, artifactId) => {
                  const download = await vm.downloadArtifact(jobId, artifactId);
                  if (download) triggerDownload(download);
                }}
              />
              <ListPagination
                page={vm.jobsPage}
                itemCount={vm.jobs.length}
                hasMore={vm.jobsHasMore}
                loading={vm.loading}
                onPrevious={vm.previousJobsPage}
                onNext={vm.nextJobsPage}
              />
            </>
          )}
        </>
      )}
    </section>
  );
}

function HarborTab({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean;
  count: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`border-b-2 px-1 pb-2 text-sm font-medium ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
      }`}
    >
      {children}
      <span className="ml-1.5 text-xs font-normal text-gray-400">{count}</span>
    </button>
  );
}

function pageSummary(page: number, itemCount: number, itemName: string): string {
  if (itemCount === 0) return `No ${itemName}s on this page`;
  const start = page * HARBOR_ADMIN_PAGE_SIZE + 1;
  const end = page * HARBOR_ADMIN_PAGE_SIZE + itemCount;
  return `Showing ${start}–${end} ${itemCount === 1 ? itemName : `${itemName}s`}`;
}

function ListPagination({
  page,
  itemCount,
  hasMore,
  loading,
  onPrevious,
  onNext,
}: {
  page: number;
  itemCount: number;
  hasMore: boolean;
  loading: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  if (page === 0 && !hasMore) return null;
  const start = page * HARBOR_ADMIN_PAGE_SIZE + 1;
  const end = page * HARBOR_ADMIN_PAGE_SIZE + itemCount;
  const buttonClass = 'rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="mt-4 flex items-center justify-between gap-3 text-sm text-gray-600">
      <span>{itemCount > 0 ? `Showing ${start}–${end}` : 'No items on this page'}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className={buttonClass}
          onClick={onPrevious}
          disabled={loading || page === 0}
        >
          Previous
        </button>
        <span>Page {page + 1}</span>
        <button
          type="button"
          className={buttonClass}
          onClick={onNext}
          disabled={loading || !hasMore}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function HarborRunnerTable({ runners }: { runners: AdminHarborRunnerToken[] }) {
  if (runners.length === 0) {
    return <EmptyState message="No Harbor runner credentials have been issued." />;
  }

  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Runner
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              ID
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Status
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Owner email
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Created
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Expires
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {runners.map((runner) => (
            <HarborRunnerRow key={runner.id} runner={runner} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HarborJobsTable({
  jobs,
  jobActionId,
  onCancel,
  onDelete,
  onDownload,
}: {
  jobs: HarborJob[];
  jobActionId: string | null;
  onCancel: (jobId: string) => Promise<void>;
  onDelete: (jobId: string) => Promise<void>;
  onDownload: (jobId: string, artifactId: HarborJobArtifactId) => Promise<void>;
}) {
  if (jobs.length === 0) {
    return <EmptyState message="No Harbor jobs have been submitted." />;
  }

  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Job ID
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Status
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Task
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Owner email
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Created
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Updated
            </th>
            <th scope="col" className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {jobs.map((job) => (
            <HarborJobRow
              key={job.id}
              job={job}
              pending={jobActionId === job.id}
              onCancel={onCancel}
              onDelete={onDelete}
              onDownload={onDownload}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
      <p className="text-sm text-gray-500">{message}</p>
    </div>
  );
}

function HarborRunnerRow({ runner }: { runner: AdminHarborRunnerToken }) {
  return (
    <tr>
      <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">{runner.label}</td>
      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-600">{runner.id}</td>
      <td className="whitespace-nowrap px-4 py-3">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(runner.status)}`}>
          {STATUS_LABELS[runner.status]}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{runner.ownerEmail ?? '—'}</td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDate(runner.createdAt)}</td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDate(runner.expiresAt)}</td>
    </tr>
  );
}

function HarborJobRow({
  job,
  pending,
  onCancel,
  onDelete,
  onDownload,
}: {
  job: HarborJob;
  pending: boolean;
  onCancel: (jobId: string) => Promise<void>;
  onDelete: (jobId: string) => Promise<void>;
  onDownload: (jobId: string, artifactId: HarborJobArtifactId) => Promise<void>;
}) {
  return (
    <tr>
      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-600">{job.id}</td>
      <td className="whitespace-nowrap px-4 py-3">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${jobStatusClass(job.status)}`}>
          {job.status}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-900">{job.taskName ?? '—'}</td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{job.ownerEmail ?? '—'}</td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDate(job.createdAt)}</td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDate(job.updatedAt)}</td>
      <td className="whitespace-nowrap px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          {job.artifacts.map((artifact) => (
            <ArtifactDownloadButton
              key={artifact.id}
              artifact={artifact}
              disabled={pending}
              onClick={() => void onDownload(job.id, artifact.id)}
            />
          ))}
          <HarborJobActionsMenu
            job={job}
            pending={pending}
            onCancel={onCancel}
            onDelete={onDelete}
          />
        </div>
      </td>
    </tr>
  );
}

function ArtifactDownloadButton({
  artifact,
  disabled,
  onClick,
}: {
  artifact: HarborJobArtifact;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`Download ${artifact.label}`}
      title={`Download ${artifact.label}`}
      className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 bg-white px-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Download className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="hidden lg:inline">{artifact.label}</span>
    </button>
  );
}

function HarborJobActionsMenu({
  job,
  pending,
  onCancel,
  onDelete,
}: {
  job: HarborJob;
  pending: boolean;
  onCancel: (jobId: string) => Promise<void>;
  onDelete: (jobId: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const terminal = isTerminalJobStatus(job.status);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={pending}
        aria-label={`Actions for ${job.id}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Job actions"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
      </button>
      {open && (
        <PortalMenu
          anchorRef={triggerRef}
          onRequestClose={() => setOpen(false)}
          role="menu"
          className="w-44 py-1"
        >
          <button
            type="button"
            role="menuitem"
            disabled={pending || terminal}
            title={terminal ? 'This job is already finished.' : undefined}
            onClick={() => {
              if (!window.confirm(`Cancel Harbor job ${job.id}?`)) return;
              setOpen(false);
              void onCancel(job.id);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <XCircle className="h-4 w-4 text-amber-600" aria-hidden="true" />
            Cancel job
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={pending || !terminal}
            title={!terminal ? 'Cancel the job before deleting it.' : undefined}
            onClick={() => {
              if (!window.confirm(`Delete Harbor job ${job.id}? This also removes its artifacts.`)) return;
              setOpen(false);
              void onDelete(job.id);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Delete job
          </button>
        </PortalMenu>
      )}
    </div>
  );
}

function statusClass(status: HarborRunnerTokenStatus): string {
  if (status === 'active') return 'bg-green-50 text-green-700';
  if (status === 'expired') return 'bg-amber-50 text-amber-700';
  return 'bg-gray-100 text-gray-600';
}

function jobStatusClass(status: string): string {
  if (['COMPLETE', 'READY'].includes(status)) return 'bg-green-50 text-green-700';
  if (['ERROR', 'ORACLE_FAILED', 'ORACLE_EXCEPTION'].includes(status)) {
    return 'bg-red-50 text-red-700';
  }
  if (['CANCELED', 'EXPIRED'].includes(status)) return 'bg-gray-100 text-gray-600';
  return 'bg-amber-50 text-amber-700';
}

function isTerminalJobStatus(status: string): boolean {
  return ['COMPLETE', 'CANCELED', 'ERROR', 'EXPIRED', 'ORACLE_FAILED', 'ORACLE_EXCEPTION'].includes(status);
}

function triggerDownload(download: HarborArtifactDownload): void {
  const link = document.createElement('a');
  link.href = download.downloadUrl;
  link.download = download.filename;
  link.rel = 'noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}
